import FitParser from "fit-file-parser";
import { computeMaximumInstantaneousPower, computePowerDurationCurve } from "../analytics/powerDuration";
import { computeRideAnalytics } from "../analytics/rideAnalytics";
import type { Ride, RideRecord } from "../types/ride";
import { createEmptyRide } from "../types/ride";
import { summarizeDecodedFitMessageCounts, type RideParserPhaseProfile } from "./rideParserProfile";
import { inferRecordingIntervalSeconds, validateRideRecords } from "./rideRecordValidation";

export function createFitParser() {
  return new FitParser({ force: true, mode: "both", speedUnit: "m/s", lengthUnit: "m", temperatureUnit: "c" });
}

export function isFitFile(file: File | { name: string }) {
  const filename = file.name.toLowerCase();
  return filename.endsWith(".fit") || filename.endsWith(".fit.gz");
}

function getValueFromObject(obj: unknown, keys: string[]): unknown | null {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return null;
  }

  for (const key of keys) {
    if (key in obj) {
      const value = (obj as Record<string, unknown>)[key];
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
  }

  return null;
}

function findValueByKeys(data: unknown, keys: string[]): unknown | null {
  if (data === null || data === undefined) {
    return null;
  }

  const stack: unknown[] = [data];

  while (stack.length > 0) {
    const current = stack.pop();
    if (current === null || current === undefined) {
      continue;
    }

    if (typeof current === "number" || typeof current === "string" || typeof current === "boolean") {
      continue;
    }

    if (Array.isArray(current)) {
      stack.push(...current);
      continue;
    }

    if (typeof current !== "object") {
      continue;
    }

    const matchedValue = getValueFromObject(current, keys);
    if (matchedValue !== null) {
      return matchedValue;
    }

    stack.push(...Object.values(current as Record<string, unknown>));
  }

  return null;
}

function collectNumericValues(value: unknown): number[] {
  if (value === null || value === undefined || value === "") {
    return [];
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? [value] : [];
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? [parsed] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(item => collectNumericValues(item));
  }

  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap(item => collectNumericValues(item));
  }

  return [];
}

const HSI_SCOPE_KEYS = new Set(["record", "records", "developer", "developerData", "developerFields", "developer_fields", "developer_data", "developerfield", "developerfields"]);
const HSI_VALUE_KEYS = ["heat_strain_index", "heatStrainIndex", "core_heat_strain_index", "coreHeatStrainIndex", "hsi"];

function isHsiScopeNode(key: string): boolean {
  return HSI_SCOPE_KEYS.has(key);
}

function collectHeatStrainIndexValues(data: unknown): number[] {
  const values: number[] = [];
  const nodeStack: unknown[] = [data];
  const scopeStack: boolean[] = [false];

  while (nodeStack.length > 0) {
    const node = nodeStack.pop();
    const inScopedContext = scopeStack.pop() === true;
    if (node === null || node === undefined || typeof node !== "object") {
      continue;
    }

    if (Array.isArray(node)) {
      for (let index = 0; index < node.length; index += 1) {
        nodeStack.push(node[index]);
        scopeStack.push(inScopedContext);
      }
      continue;
    }

    const entry = node as Record<string, unknown>;
    if (inScopedContext) {
      const candidateValue = getValueFromObject(entry, HSI_VALUE_KEYS);
      if (candidateValue !== null) {
        const numericValue = toNumber(candidateValue);
        if (numericValue !== null && numericValue > 0) {
          values.push(numericValue);
        }
      }
    }

    const keys = Object.keys(entry);
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      const value = entry[key];
      if (value === null || typeof value !== "object") {
        continue;
      }
      nodeStack.push(value);
      scopeStack.push(inScopedContext || isHsiScopeNode(key));
    }
  }

  return values;
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
}

function normalizeTimestamp(value: unknown): string | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const nowMs = Date.now();
    const numericValue = value;

    const candidates: number[] = [];
    if (Math.abs(numericValue) >= 1e12) {
      candidates.push(numericValue);
    } else {
      candidates.push(numericValue * 1000);
      candidates.push((numericValue + 631065600) * 1000);
    }

    const validCandidates = candidates
      .map(candidate => ({
        ms: candidate,
        date: new Date(candidate)
      }))
      .filter(candidate => !Number.isNaN(candidate.date.getTime()))
      .filter(candidate => {
        const year = candidate.date.getUTCFullYear();
        return year >= 1990 && year <= 2100;
      });

    if (validCandidates.length === 0) {
      return null;
    }

    const bestCandidate = validCandidates.reduce((best, candidate) => {
      const bestDistance = Math.abs(best.ms - nowMs);
      const candidateDistance = Math.abs(candidate.ms - nowMs);
      return candidateDistance < bestDistance ? candidate : best;
    });

    return bestCandidate.date.toISOString();
  }

  if (typeof value === "string" && value.trim() !== "") {
    const numericStringValue = Number(value);
    if (!Number.isNaN(numericStringValue)) {
      return normalizeTimestamp(numericStringValue);
    }

    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toISOString();
    }
  }

  return null;
}

function normalizeCoordinate(value: unknown): number | null {
  const numericValue = toNumber(value);
  if (numericValue === null) {
    return null;
  }

  if (Math.abs(numericValue) > 180) {
    return numericValue / 11930465;
  }

  return numericValue;
}

function isRecordLikeObject(node: unknown): node is Record<string, unknown> {
  if (!node || typeof node !== "object" || Array.isArray(node)) {
    return false;
  }

  const entry = node as Record<string, unknown>;
  for (let index = 0; index < RECORD_SIGNAL_KEYS.length; index += 1) {
    const key = RECORD_SIGNAL_KEYS[index];
    if (!(key in entry)) {
      continue;
    }
    const value = entry[key];
    if (value !== null && value !== undefined && value !== "") {
      return true;
    }
  }

  return false;
}

function isNumericKeyedObject(node: unknown): node is Record<string, unknown> {
  if (!node || typeof node !== "object" || Array.isArray(node)) {
    return false;
  }

  const keys = Object.keys(node as Record<string, unknown>);
  if (keys.length === 0) {
    return false;
  }

  return keys.every(key => /^\d+$/.test(key));
}

function normalizeRecordCollection(node: unknown): unknown[] {
  if (Array.isArray(node)) {
    return node;
  }

  if (isNumericKeyedObject(node)) {
    return Object.entries(node)
      .sort((left, right) => Number(left[0]) - Number(right[0]))
      .map(([, value]) => value);
  }

  if (node && typeof node === "object") {
    return Object.values(node as Record<string, unknown>);
  }

  return [];
}

const EXCLUDED_MESSAGE_PATH_SEGMENTS = new Set([
  "session",
  "sessions",
  "lap",
  "laps",
  "event",
  "events",
  "deviceinfo",
  "deviceinfos",
  "device",
  "developerdata",
  "developerdatas",
  "developerfield",
  "developerfields"
]);

const normalizedPathSegmentCache = new Map<string, string>();

function normalizePathSegment(segment: string): string {
  const cached = normalizedPathSegmentCache.get(segment);
  if (cached !== undefined) {
    return cached;
  }
  const normalized = segment.toLowerCase().replace(/[^a-z0-9]/g, "");
  normalizedPathSegmentCache.set(segment, normalized);
  return normalized;
}

function isExcludedMessagePathSegment(segment: string): boolean {
  return EXCLUDED_MESSAGE_PATH_SEGMENTS.has(normalizePathSegment(segment));
}

const RECORD_SIGNAL_KEYS = [
  "timestamp",
  "time_stamp",
  "power",
  "heart_rate",
  "heartRate",
  "cadence",
  "distance",
  "speed",
  "enhanced_speed",
  "position_lat",
  "positionLat",
  "position_long",
  "positionLong",
  "altitude",
  "enhanced_altitude"
];

const RIDE_RECORD_SIGNATURE_KEYS: Array<keyof RideRecord> = [
  "timestamp",
  "elapsedSeconds",
  "distanceMeters",
  "speedMetersPerSecond",
  "powerWatts",
  "heartRateBpm",
  "cadenceRpm",
  "altitudeMeters",
  "latitudeDegrees",
  "longitudeDegrees",
  "temperatureCelsius",
  "coreTemperatureCelsius",
  "heatStrainIndex",
  "leftRightBalance",
  "torqueEffectivenessLeft",
  "torqueEffectivenessRight",
  "pedalSmoothnessLeft",
  "pedalSmoothnessRight",
  "vehicleCount"
];

/** Fixed-order primitive join; equivalent discriminating power to JSON.stringify at a fraction of the cost. */
function createRideRecordSignature(record: RideRecord): string {
  let signature = "";
  for (let index = 0; index < RIDE_RECORD_SIGNATURE_KEYS.length; index += 1) {
    const value = record[RIDE_RECORD_SIGNATURE_KEYS[index]];
    signature += (value === null || value === undefined ? "" : String(value)) + "\u0001";
  }
  return signature;
}

/** A deep search over a record with no nested object values is equivalent to a shallow lookup. */
function hasNestedObjectValue(entry: Record<string, unknown>): boolean {
  for (const key of Object.keys(entry)) {
    const value = entry[key];
    if (value !== null && typeof value === "object" && !(value instanceof Date)) {
      return true;
    }
  }
  return false;
}

const CORE_TEMPERATURE_DEEP_KEYS = ["core_temperature", "coreTemperature", "core_temperature_celsius", "coreTemperatureCelsius", "core_temp", "coreTemp", "core body temperature", "core_body_temperature", "coreBodyTemperature"];

function mapToRideRecord(recordEntries: Record<string, unknown>): RideRecord {
  const shallowRecord: RideRecord = {};
  const searchNested = hasNestedObjectValue(recordEntries);

  const timestamp = getValueFromObject(recordEntries, ["timestamp", "timeStamp", "time_stamp", "start_time", "startTime", "local_timestamp", "localTimestamp", "timestamp_ms", "timestampMs"]);
  const elapsedSeconds = getValueFromObject(recordEntries, ["elapsed_seconds", "elapsedSeconds", "elapsed_time", "elapsedTime"]);
  const distanceMeters = getValueFromObject(recordEntries, ["distance", "distance_meters", "distanceMeters"]);
  const speedMetersPerSecond = getValueFromObject(recordEntries, ["speed", "speed_meters_per_second", "speedMetersPerSecond", "enhanced_speed", "enhancedSpeed"]);
  const powerWatts = getValueFromObject(recordEntries, ["power", "power_watts", "powerWatts"]);
  const heartRateBpm = getValueFromObject(recordEntries, ["heart_rate", "heartRate", "heart_rate_bpm", "heartRateBpm"]);
  const cadenceRpm = getValueFromObject(recordEntries, ["cadence", "cadence_rpm", "cadenceRpm"]);
  const altitudeMeters = getValueFromObject(recordEntries, ["enhanced_altitude", "enhancedAltitude", "altitude", "altitude_meters", "altitudeMeters"]);
  const latitudeDegrees = getValueFromObject(recordEntries, ["position_lat", "positionLat", "latitude", "latitude_degrees", "latitudeDegrees"]);
  const longitudeDegrees = getValueFromObject(recordEntries, ["position_long", "positionLong", "longitude", "longitude_degrees", "longitudeDegrees"]);
  const temperatureCelsius = getValueFromObject(recordEntries, ["temperature", "temperature_celsius", "temperatureCelsius"]);
  const coreTemperatureCelsius =
    getValueFromObject(recordEntries, ["core_temperature", "coreTemperature", "core_temperature_celsius", "coreTemperatureCelsius"])
    ?? (searchNested ? findValueByKeys(recordEntries, CORE_TEMPERATURE_DEEP_KEYS) : getValueFromObject(recordEntries, CORE_TEMPERATURE_DEEP_KEYS));
  const heatStrainIndex =
    getValueFromObject(recordEntries, HSI_VALUE_KEYS)
    ?? (searchNested ? findValueByKeys(recordEntries, HSI_VALUE_KEYS) : null);
  const leftRightBalance = getValueFromObject(recordEntries, ["left_right_balance", "leftRightBalance"]);
  const torqueEffectivenessLeft = getValueFromObject(recordEntries, ["torque_effectiveness_left", "torqueEffectivenessLeft"]);
  const torqueEffectivenessRight = getValueFromObject(recordEntries, ["torque_effectiveness_right", "torqueEffectivenessRight"]);
  const pedalSmoothnessLeft = getValueFromObject(recordEntries, ["pedal_smoothness_left", "pedalSmoothnessLeft"]);
  const pedalSmoothnessRight = getValueFromObject(recordEntries, ["pedal_smoothness_right", "pedalSmoothnessRight"]);
  const vehicleCount = getValueFromObject(recordEntries, ["vehicle_count", "vehicleCount", "num_vehicles", "numVehicles"]);

  shallowRecord.timestamp = normalizeTimestamp(timestamp);
  shallowRecord.elapsedSeconds = toNumber(elapsedSeconds);
  shallowRecord.distanceMeters = toNumber(distanceMeters);
  shallowRecord.speedMetersPerSecond = toNumber(speedMetersPerSecond);
  shallowRecord.powerWatts = toNumber(powerWatts);
  shallowRecord.heartRateBpm = toNumber(heartRateBpm);
  shallowRecord.cadenceRpm = toNumber(cadenceRpm);
  shallowRecord.altitudeMeters = toNumber(altitudeMeters);
  shallowRecord.latitudeDegrees = normalizeCoordinate(latitudeDegrees);
  shallowRecord.longitudeDegrees = normalizeCoordinate(longitudeDegrees);
  shallowRecord.temperatureCelsius = toNumber(temperatureCelsius);
  shallowRecord.coreTemperatureCelsius = toNumber(coreTemperatureCelsius);
  shallowRecord.heatStrainIndex = toNumber(heatStrainIndex);
  shallowRecord.leftRightBalance = toNumber(leftRightBalance);
  shallowRecord.torqueEffectivenessLeft = toNumber(torqueEffectivenessLeft);
  shallowRecord.torqueEffectivenessRight = toNumber(torqueEffectivenessRight);
  shallowRecord.pedalSmoothnessLeft = toNumber(pedalSmoothnessLeft);
  shallowRecord.pedalSmoothnessRight = toNumber(pedalSmoothnessRight);
  shallowRecord.vehicleCount = toNumber(vehicleCount);

  return shallowRecord;
}

function collectCollectionDiagnostics(data: unknown): Record<string, { keys: string[]; length: number | null }> {
  const details: Record<string, { keys: string[]; length: number | null }> = {};

  const likelyCollectionKeys = ["records", "record", "activity", "sessions", "laps", "messages", "data"];
  const root = data && typeof data === "object" && !Array.isArray(data) ? (data as Record<string, unknown>) : null;

  for (const key of likelyCollectionKeys) {
    const value = root ? root[key] : undefined;
    if (value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      const first = value.find(item => item && typeof item === "object" && !Array.isArray(item)) as Record<string, unknown> | undefined;
      details[key] = {
        keys: first ? Object.keys(first).slice(0, 25) : [],
        length: value.length
      };
      continue;
    }

    if (isNumericKeyedObject(value)) {
      const normalized = normalizeRecordCollection(value);
      const first = normalized.find(item => item && typeof item === "object" && !Array.isArray(item)) as Record<string, unknown> | undefined;
      details[key] = {
        keys: first ? Object.keys(first).slice(0, 25) : [],
        length: normalized.length
      };
      continue;
    }

    if (value && typeof value === "object") {
      const objectValue = value as Record<string, unknown>;
      details[key] = {
        keys: Object.keys(objectValue).slice(0, 25),
        length: null
      };
    }
  }

  return details;
}

type TimingNormalizationResult = {
  records: RideRecord[];
  recordingIntervalSeconds: number | null;
  timingFallbackUsed: boolean;
  timingNormalizationError: string | null;
};

type RecordDurationSummary = {
  elapsedSeconds: number | null;
  movingSeconds: number | null;
};

type RecordDerivedMetrics = {
  distanceMeters: number | null;
  usedGpsFallbackForDistance: boolean;
  averageMovingSpeedMetersPerSecond: number | null;
  maximumSpeedMetersPerSecond: number | null;
  averageHeartRateBpm: number | null;
  maximumHeartRateBpm: number | null;
  estimatedCaloriesKcal: number | null;
  hasSubstantialMovement: boolean;
};

const LONG_GAP_MULTIPLIER = 5;
const DURATION_MISMATCH_PERCENT = 0.35;
const DURATION_MISMATCH_SECONDS = 600;
const DISTANCE_MISMATCH_PERCENT = 0.35;
const DISTANCE_MISMATCH_METERS = 1000;
const SPEED_MISMATCH_PERCENT = 0.3;
const SPEED_MISMATCH_METERS_PER_SECOND = 1.5;
const HEART_RATE_MISMATCH_BPM = 12;
const CALORIES_MISMATCH_KCAL = 100;
const MAX_AVERAGE_SPEED_METERS_PER_SECOND = 25;
const MAX_MAXIMUM_SPEED_METERS_PER_SECOND = 45;
const MAX_REASONABLE_DISTANCE_METERS = 600000;
const SUBSTANTIAL_MOVEMENT_DISTANCE_METERS = 1500;
const MIN_GPS_SEGMENT_SECONDS = 0.5;

function toRecordTimeSeconds(record: RideRecord): number | null {
  if (typeof record.elapsedSeconds === "number" && Number.isFinite(record.elapsedSeconds)) {
    return record.elapsedSeconds;
  }

  if (record.timestamp) {
    const parsedMilliseconds = Date.parse(record.timestamp);
    if (!Number.isNaN(parsedMilliseconds)) {
      return parsedMilliseconds / 1000;
    }
  }

  return null;
}

function haversineDistanceMeters(leftLatitude: number, leftLongitude: number, rightLatitude: number, rightLongitude: number): number {
  const earthRadiusMeters = 6371000;
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

  const latitudeDelta = toRadians(rightLatitude - leftLatitude);
  const longitudeDelta = toRadians(rightLongitude - leftLongitude);
  const latitudeLeftRadians = toRadians(leftLatitude);
  const latitudeRightRadians = toRadians(rightLatitude);

  const haversineA =
    Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
    Math.cos(latitudeLeftRadians) * Math.cos(latitudeRightRadians) * Math.sin(longitudeDelta / 2) * Math.sin(longitudeDelta / 2);
  const haversineC = 2 * Math.atan2(Math.sqrt(haversineA), Math.sqrt(1 - haversineA));
  return earthRadiusMeters * haversineC;
}

function isValidCoordinate(latitudeDegrees: unknown, longitudeDegrees: unknown): boolean {
  if (typeof latitudeDegrees !== "number" || typeof longitudeDegrees !== "number") {
    return false;
  }

  if (!Number.isFinite(latitudeDegrees) || !Number.isFinite(longitudeDegrees)) {
    return false;
  }

  return Math.abs(latitudeDegrees) <= 90 && Math.abs(longitudeDegrees) <= 180;
}

function deriveDistanceFromCumulativeRecords(records: RideRecord[], movingTimeSeconds: number | null): number | null {
  const cumulativeValues = records
    .map(record => toNumber(record.distanceMeters))
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0);

  if (cumulativeValues.length < 2) {
    return null;
  }

  const firstDistance = cumulativeValues[0];
  const lastDistance = cumulativeValues[cumulativeValues.length - 1];
  const rawDelta = lastDistance - firstDistance;
  if (rawDelta <= 0) {
    return null;
  }

  const candidates = [rawDelta, rawDelta * 1000];
  const medianRecordSpeed = (() => {
    const speedValues = records
      .map(record => toNumber(record.speedMetersPerSecond))
      .filter((value): value is number => typeof value === "number" && Number.isFinite(value) && value > 0 && value <= MAX_MAXIMUM_SPEED_METERS_PER_SECOND)
      .sort((left, right) => left - right);

    if (speedValues.length === 0) {
      return null;
    }

    const middle = Math.floor(speedValues.length / 2);
    return speedValues.length % 2 === 1 ? speedValues[middle] : (speedValues[middle - 1] + speedValues[middle]) / 2;
  })();

  const expectedDistanceMeters =
    movingTimeSeconds !== null && movingTimeSeconds > 0 && medianRecordSpeed !== null
      ? movingTimeSeconds * medianRecordSpeed
      : null;

  const rankedCandidates = candidates
    .filter(candidate => candidate > 0 && candidate <= MAX_REASONABLE_DISTANCE_METERS)
    .map(candidate => {
      let score = 0;

      if (expectedDistanceMeters !== null && expectedDistanceMeters > 0) {
        const delta = Math.abs(candidate - expectedDistanceMeters);
        score += delta / Math.max(expectedDistanceMeters, 1);
      }

      if (movingTimeSeconds !== null && movingTimeSeconds > 0) {
        const averageSpeed = candidate / movingTimeSeconds;
        if (averageSpeed > MAX_AVERAGE_SPEED_METERS_PER_SECOND) {
          score += 10;
        }
      }

      if (candidate === rawDelta * 1000) {
        score += 0.1;
      }

      return { candidate, score };
    })
    .sort((left, right) => left.score - right.score);

  return rankedCandidates.length > 0 ? rankedCandidates[0].candidate : null;
}

function deriveDistanceFromGps(records: RideRecord[]): number | null {
  if (records.length < 2) {
    return null;
  }

  let totalDistanceMeters = 0;
  for (let index = 1; index < records.length; index += 1) {
    const previous = records[index - 1];
    const current = records[index];

    if (!isValidCoordinate(previous.latitudeDegrees, previous.longitudeDegrees) || !isValidCoordinate(current.latitudeDegrees, current.longitudeDegrees)) {
      continue;
    }

    const segmentDistanceMeters = haversineDistanceMeters(
      previous.latitudeDegrees as number,
      previous.longitudeDegrees as number,
      current.latitudeDegrees as number,
      current.longitudeDegrees as number
    );

    if (!Number.isFinite(segmentDistanceMeters) || segmentDistanceMeters <= 0) {
      continue;
    }

    const previousTime = toRecordTimeSeconds(previous);
    const currentTime = toRecordTimeSeconds(current);
    if (previousTime !== null && currentTime !== null) {
      const deltaSeconds = currentTime - previousTime;
      if (deltaSeconds >= MIN_GPS_SEGMENT_SECONDS) {
        const impliedSpeed = segmentDistanceMeters / deltaSeconds;
        if (impliedSpeed > MAX_MAXIMUM_SPEED_METERS_PER_SECOND) {
          continue;
        }
      }
    }

    totalDistanceMeters += segmentDistanceMeters;
  }

  return totalDistanceMeters > 0 ? totalDistanceMeters : null;
}

function normalizeSummarySpeedMetersPerSecond(
  rawSpeed: number | null,
  referenceSpeedMetersPerSecond: number | null,
  isMaximumSpeed: boolean
): number | null {
  if (rawSpeed === null || !Number.isFinite(rawSpeed) || rawSpeed <= 0) {
    return null;
  }

  const speedLimit = isMaximumSpeed ? MAX_MAXIMUM_SPEED_METERS_PER_SECOND : MAX_AVERAGE_SPEED_METERS_PER_SECOND;
  const candidates = [
    { value: rawSpeed, penalty: 0 },
    { value: rawSpeed / 3.6, penalty: 0.1 }
  ];

  const ranked = candidates
    .filter(candidate => candidate.value > 0 && Number.isFinite(candidate.value) && candidate.value <= speedLimit)
    .map(candidate => {
      let score = candidate.penalty;
      if (referenceSpeedMetersPerSecond !== null && referenceSpeedMetersPerSecond > 0) {
        score += Math.abs(candidate.value - referenceSpeedMetersPerSecond) / referenceSpeedMetersPerSecond;
      }
      return { ...candidate, score };
    })
    .sort((left, right) => left.score - right.score);

  return ranked.length > 0 ? ranked[0].value : null;
}

function normalizeSummaryDistanceMeters(rawDistance: number | null, referenceDistanceMeters: number | null, movingTimeSeconds: number | null): number | null {
  if (rawDistance === null || !Number.isFinite(rawDistance) || rawDistance <= 0) {
    return null;
  }

  const candidates = [
    { value: rawDistance, penalty: 0 },
    { value: rawDistance * 1000, penalty: 0.1 }
  ];

  const ranked = candidates
    .filter(candidate => candidate.value > 0 && candidate.value <= MAX_REASONABLE_DISTANCE_METERS)
    .map(candidate => {
      let score = candidate.penalty;
      if (referenceDistanceMeters !== null && referenceDistanceMeters > 0) {
        score += Math.abs(candidate.value - referenceDistanceMeters) / referenceDistanceMeters;
      }
      if (movingTimeSeconds !== null && movingTimeSeconds > 0) {
        const impliedAverageSpeed = candidate.value / movingTimeSeconds;
        if (impliedAverageSpeed > MAX_AVERAGE_SPEED_METERS_PER_SECOND) {
          score += 10;
        }
      }
      return { ...candidate, score };
    })
    .sort((left, right) => left.score - right.score);

  return ranked.length > 0 ? ranked[0].value : null;
}

function isConsistentWithReference(summaryValue: number | null, referenceValue: number | null, absoluteThreshold: number, percentThreshold: number): boolean {
  if (summaryValue === null || referenceValue === null || referenceValue <= 0) {
    return false;
  }

  const delta = Math.abs(summaryValue - referenceValue);
  const threshold = Math.max(absoluteThreshold, referenceValue * percentThreshold);
  return delta <= threshold;
}

function deriveRecordMetrics(records: RideRecord[], movingTimeSeconds: number | null, recordingIntervalSeconds: number | null): RecordDerivedMetrics {
  const distanceFromCumulative = deriveDistanceFromCumulativeRecords(records, movingTimeSeconds);
  const distanceFromGps = distanceFromCumulative === null ? deriveDistanceFromGps(records) : null;
  const distanceMeters = distanceFromCumulative ?? distanceFromGps;

  const speedValues = records
    .map(record => toNumber(record.speedMetersPerSecond))
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value) && value > 0 && value <= MAX_MAXIMUM_SPEED_METERS_PER_SECOND);
  const maximumSpeedMetersPerSecond = speedValues.length > 0 ? Math.max(...speedValues) : null;

  const averageMovingSpeedMetersPerSecond =
    distanceMeters !== null && movingTimeSeconds !== null && movingTimeSeconds > 0
      ? distanceMeters / movingTimeSeconds
      : null;

  const heartRateValues = records
    .map(record => toNumber(record.heartRateBpm))
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value) && value > 0 && value <= 240);
  const averageHeartRateBpm =
    heartRateValues.length > 0
      ? heartRateValues.reduce((sum, value) => sum + value, 0) / heartRateValues.length
      : null;
  const maximumHeartRateBpm = heartRateValues.length > 0 ? Math.max(...heartRateValues) : null;

  const ordered = [...records].sort((left, right) => {
    const leftTime = toRecordTimeSeconds(left);
    const rightTime = toRecordTimeSeconds(right);
    if (leftTime === null || rightTime === null) {
      return 0;
    }
    return leftTime - rightTime;
  });

  const inferredIntervalSeconds = inferRecordingIntervalSeconds(ordered) ?? recordingIntervalSeconds;
  const longGapThresholdSeconds =
    inferredIntervalSeconds && inferredIntervalSeconds > 0
      ? inferredIntervalSeconds * LONG_GAP_MULTIPLIER
      : Number.POSITIVE_INFINITY;

  let estimatedWorkJoules = 0;
  for (let index = 0; index < ordered.length - 1; index += 1) {
    const current = ordered[index];
    const next = ordered[index + 1];
    const currentTime = toRecordTimeSeconds(current);
    const nextTime = toRecordTimeSeconds(next);

    if (currentTime === null || nextTime === null) {
      continue;
    }

    const deltaSeconds = nextTime - currentTime;
    if (deltaSeconds <= 0 || deltaSeconds > longGapThresholdSeconds) {
      continue;
    }

    const powerWatts = toNumber(current.powerWatts);
    if (powerWatts === null || !Number.isFinite(powerWatts) || powerWatts <= 0) {
      continue;
    }

    estimatedWorkJoules += powerWatts * deltaSeconds;
  }

  const estimatedCaloriesKcal = estimatedWorkJoules > 0 ? estimatedWorkJoules / 1000 : null;
  const hasSubstantialMovement =
    (distanceMeters !== null && distanceMeters >= SUBSTANTIAL_MOVEMENT_DISTANCE_METERS) ||
    (movingTimeSeconds !== null && movingTimeSeconds >= 1800 && averageMovingSpeedMetersPerSecond !== null && averageMovingSpeedMetersPerSecond > 1);

  return {
    distanceMeters,
    usedGpsFallbackForDistance: distanceFromCumulative === null && distanceFromGps !== null,
    averageMovingSpeedMetersPerSecond,
    maximumSpeedMetersPerSecond,
    averageHeartRateBpm,
    maximumHeartRateBpm,
    estimatedCaloriesKcal,
    hasSubstantialMovement
  };
}

function reconcileSummaryMetricsWithRecords(
  ride: Ride,
  summaryDistanceRawMetersOrKilometers: number | null,
  summaryAverageSpeedRawMetersPerSecondOrKilometersPerHour: number | null,
  summaryMaximumSpeedRawMetersPerSecondOrKilometersPerHour: number | null,
  recordMetrics: RecordDerivedMetrics
): { replacedDistance: boolean; replacedAverageSpeed: boolean; replacedMaximumSpeed: boolean; replacedHeartRate: boolean; replacedCalories: boolean } {
  const normalizedSummaryDistance = normalizeSummaryDistanceMeters(summaryDistanceRawMetersOrKilometers, recordMetrics.distanceMeters, ride.movingTime);
  const normalizedSummaryAverageSpeed = normalizeSummarySpeedMetersPerSecond(
    summaryAverageSpeedRawMetersPerSecondOrKilometersPerHour,
    recordMetrics.averageMovingSpeedMetersPerSecond,
    false
  );
  const normalizedSummaryMaximumSpeed = normalizeSummarySpeedMetersPerSecond(
    summaryMaximumSpeedRawMetersPerSecondOrKilometersPerHour,
    recordMetrics.maximumSpeedMetersPerSecond,
    true
  );

  const canTrustSummaryDistance =
    normalizedSummaryDistance !== null &&
    normalizedSummaryDistance > 0 &&
    normalizedSummaryDistance <= MAX_REASONABLE_DISTANCE_METERS &&
    (recordMetrics.distanceMeters === null ||
      isConsistentWithReference(normalizedSummaryDistance, recordMetrics.distanceMeters, DISTANCE_MISMATCH_METERS, DISTANCE_MISMATCH_PERCENT));

  const canTrustSummaryAverageSpeed =
    normalizedSummaryAverageSpeed !== null &&
    normalizedSummaryAverageSpeed > 0 &&
    normalizedSummaryAverageSpeed <= MAX_AVERAGE_SPEED_METERS_PER_SECOND &&
    (recordMetrics.averageMovingSpeedMetersPerSecond === null ||
      isConsistentWithReference(
        normalizedSummaryAverageSpeed,
        recordMetrics.averageMovingSpeedMetersPerSecond,
        SPEED_MISMATCH_METERS_PER_SECOND,
        SPEED_MISMATCH_PERCENT
      ));

  const canTrustSummaryMaximumSpeed =
    normalizedSummaryMaximumSpeed !== null &&
    normalizedSummaryMaximumSpeed > 0 &&
    normalizedSummaryMaximumSpeed <= MAX_MAXIMUM_SPEED_METERS_PER_SECOND &&
    (recordMetrics.maximumSpeedMetersPerSecond === null ||
      isConsistentWithReference(
        normalizedSummaryMaximumSpeed,
        recordMetrics.maximumSpeedMetersPerSecond,
        SPEED_MISMATCH_METERS_PER_SECOND,
        SPEED_MISMATCH_PERCENT
      ));

  const previousDistance = ride.distance;
  const previousAverageSpeed = ride.averageSpeed;
  const previousMaximumSpeed = ride.maximumSpeed;
  const previousAverageHeartRate = ride.averageHeartRate;
  const previousMaximumHeartRate = ride.maxHeartRate;
  const previousCalories = ride.calories;

  ride.distance = canTrustSummaryDistance ? normalizedSummaryDistance : recordMetrics.distanceMeters;
  if (ride.distance === null && normalizedSummaryDistance !== null) {
    ride.distance = normalizedSummaryDistance;
  }

  ride.averageSpeed = canTrustSummaryAverageSpeed ? normalizedSummaryAverageSpeed : recordMetrics.averageMovingSpeedMetersPerSecond;
  if (ride.averageSpeed === null && normalizedSummaryAverageSpeed !== null) {
    ride.averageSpeed = normalizedSummaryAverageSpeed;
  }

  ride.maximumSpeed = canTrustSummaryMaximumSpeed ? normalizedSummaryMaximumSpeed : recordMetrics.maximumSpeedMetersPerSecond;
  if (ride.maximumSpeed === null && normalizedSummaryMaximumSpeed !== null) {
    ride.maximumSpeed = normalizedSummaryMaximumSpeed;
  }

  // When distance and moving time are valid, a zero average speed is internally inconsistent.
  if (
    ride.averageSpeed !== null &&
    ride.averageSpeed <= 0 &&
    ride.distance !== null &&
    ride.distance > 0 &&
    ride.movingTime !== null &&
    ride.movingTime > 0
  ) {
    ride.averageSpeed = ride.distance / ride.movingTime;
  }

  if (recordMetrics.hasSubstantialMovement && ride.distance !== null && ride.distance < 100 && recordMetrics.distanceMeters !== null) {
    ride.distance = recordMetrics.distanceMeters;
  }

  if (ride.averageSpeed !== null && ride.averageSpeed > MAX_AVERAGE_SPEED_METERS_PER_SECOND) {
    ride.averageSpeed = recordMetrics.averageMovingSpeedMetersPerSecond;
  }

  if (ride.maximumSpeed !== null && ride.maximumSpeed > MAX_MAXIMUM_SPEED_METERS_PER_SECOND) {
    ride.maximumSpeed = recordMetrics.maximumSpeedMetersPerSecond;
  }

  const summaryAverageHeartRate = toNumber(ride.averageHeartRate);
  const summaryMaximumHeartRate = toNumber(ride.maxHeartRate);

  if (
    recordMetrics.averageHeartRateBpm !== null &&
    (summaryAverageHeartRate === null ||
      summaryAverageHeartRate <= 0 ||
      Math.abs(summaryAverageHeartRate - recordMetrics.averageHeartRateBpm) > HEART_RATE_MISMATCH_BPM)
  ) {
    ride.averageHeartRate = recordMetrics.averageHeartRateBpm;
  }

  if (
    recordMetrics.maximumHeartRateBpm !== null &&
    (summaryMaximumHeartRate === null ||
      summaryMaximumHeartRate <= 0 ||
      summaryMaximumHeartRate > 240 ||
      summaryMaximumHeartRate < recordMetrics.maximumHeartRateBpm - HEART_RATE_MISMATCH_BPM)
  ) {
    ride.maxHeartRate = recordMetrics.maximumHeartRateBpm;
  }

  const summaryCalories = toNumber(ride.calories);
  if (
    recordMetrics.estimatedCaloriesKcal !== null &&
    (summaryCalories === null ||
      summaryCalories <= 0 ||
      summaryCalories < Math.max(20, recordMetrics.estimatedCaloriesKcal * 0.2) ||
      Math.abs(summaryCalories - recordMetrics.estimatedCaloriesKcal) > CALORIES_MISMATCH_KCAL)
  ) {
    ride.calories = Math.round(recordMetrics.estimatedCaloriesKcal);
  }

  return {
    replacedDistance: previousDistance !== ride.distance,
    replacedAverageSpeed: previousAverageSpeed !== ride.averageSpeed,
    replacedMaximumSpeed: previousMaximumSpeed !== ride.maximumSpeed,
    replacedHeartRate: previousAverageHeartRate !== ride.averageHeartRate || previousMaximumHeartRate !== ride.maxHeartRate,
    replacedCalories: previousCalories !== ride.calories
  };
}

function deriveElapsedDurationFromRecordTimestamps(records: RideRecord[]): number | null {
  const parsedTimestamps = records
    .map(record => {
      if (!record.timestamp) {
        return Number.NaN;
      }

      return Date.parse(record.timestamp);
    })
    .filter(timestamp => Number.isFinite(timestamp))
    .sort((left, right) => left - right);

  if (parsedTimestamps.length < 2) {
    return null;
  }

  const firstTimestamp = parsedTimestamps[0];
  const lastTimestamp = parsedTimestamps[parsedTimestamps.length - 1];
  const elapsedSeconds = Math.max(0, (lastTimestamp - firstTimestamp) / 1000);
  return Number.isFinite(elapsedSeconds) ? elapsedSeconds : null;
}

function normalizeRecordTiming(records: RideRecord[], elapsedTimeSeconds: number | null, movingTimeSeconds: number | null): TimingNormalizationResult {
  if (records.length === 0) {
    return {
      records,
      recordingIntervalSeconds: null,
      timingFallbackUsed: false,
      timingNormalizationError: "No ride records extracted."
    };
  }

  const copiedRecords = records.map(record => ({ ...record }));
  const timestampMs = copiedRecords.map(record => {
    if (!record.timestamp) {
      return null;
    }

    const parsed = Date.parse(record.timestamp);
    return Number.isNaN(parsed) ? null : parsed;
  });

  const firstTimestampIndex = timestampMs.findIndex(timestamp => timestamp !== null);
  const hasNormalizedTimestamps = firstTimestampIndex >= 0;

  if (hasNormalizedTimestamps) {
    const firstTimestampMs = timestampMs[firstTimestampIndex] as number;
    let previousElapsedSeconds = 0;
    let hasAssignedElapsed = false;
    const inferredInterval = inferRecordingIntervalSeconds(copiedRecords);
    let usedFallbackForMissingTimestamp = false;

    copiedRecords.forEach((record, index) => {
      const currentTimestampMs = timestampMs[index];
      if (currentTimestampMs !== null) {
        const elapsedSecondsFromTimestamp = Math.max(0, (currentTimestampMs - firstTimestampMs) / 1000);
        const normalizedElapsedSeconds = hasAssignedElapsed
          ? Math.max(previousElapsedSeconds, elapsedSecondsFromTimestamp)
          : elapsedSecondsFromTimestamp;

        record.elapsedSeconds = normalizedElapsedSeconds;
        previousElapsedSeconds = normalizedElapsedSeconds;
        hasAssignedElapsed = true;
        return;
      }

      if (hasAssignedElapsed && inferredInterval !== null && inferredInterval > 0) {
        previousElapsedSeconds += inferredInterval;
        record.elapsedSeconds = previousElapsedSeconds;
        usedFallbackForMissingTimestamp = true;
      }
    });

    return {
      records: copiedRecords,
      recordingIntervalSeconds: inferRecordingIntervalSeconds(copiedRecords),
      timingFallbackUsed: usedFallbackForMissingTimestamp,
      timingNormalizationError: null
    };
  }

  let fallbackIntervalSeconds = inferRecordingIntervalSeconds(copiedRecords);
  if ((fallbackIntervalSeconds === null || fallbackIntervalSeconds <= 0) && copiedRecords.length > 1) {
    const summaryDuration = elapsedTimeSeconds ?? movingTimeSeconds;
    if (summaryDuration !== null && summaryDuration > 0) {
      fallbackIntervalSeconds = summaryDuration / (copiedRecords.length - 1);
    }
  }

  if (fallbackIntervalSeconds !== null && fallbackIntervalSeconds > 0) {
    copiedRecords.forEach((record, index) => {
      record.elapsedSeconds = index * fallbackIntervalSeconds!;
    });

    return {
      records: copiedRecords,
      recordingIntervalSeconds: fallbackIntervalSeconds,
      timingFallbackUsed: true,
      timingNormalizationError: "No record timestamps could be normalized. Using inferred interval fallback."
    };
  }

  return {
    records: copiedRecords,
    recordingIntervalSeconds: null,
    timingFallbackUsed: false,
    timingNormalizationError: "No record timestamps could be normalized."
  };
}

function collectAnalyticsValidationWarnings(ride: Ride): string[] {
  const warnings: string[] = [];
  const oneSecondPower = ride.powerDurationCurve?.find(point => point.durationSeconds === 1)?.bestAveragePower ?? null;

  if (
    typeof ride.maximumPower === "number" &&
    Number.isFinite(ride.maximumPower) &&
    typeof oneSecondPower === "number" &&
    Number.isFinite(oneSecondPower) &&
    ride.maximumPower < oneSecondPower
  ) {
    warnings.push(
      `Maximum power (${ride.maximumPower.toFixed(1)} W) is lower than 1-second power (${oneSecondPower.toFixed(1)} W).`
    );
  }

  return warnings;
}

function deriveDurationsFromRecords(records: RideRecord[], recordingIntervalSeconds: number | null): RecordDurationSummary {
  const timestampElapsedSeconds = deriveElapsedDurationFromRecordTimestamps(records);

  if (records.length < 2) {
    return {
      elapsedSeconds: timestampElapsedSeconds,
      movingSeconds: timestampElapsedSeconds
    };
  }

  const withElapsed = records.filter(record => typeof record.elapsedSeconds === "number" && Number.isFinite(record.elapsedSeconds));
  if (withElapsed.length < 2) {
    return {
      elapsedSeconds: timestampElapsedSeconds,
      movingSeconds: timestampElapsedSeconds
    };
  }

  const ordered = [...withElapsed].sort((left, right) => (left.elapsedSeconds as number) - (right.elapsedSeconds as number));
  const firstElapsed = ordered[0].elapsedSeconds as number;
  const lastElapsed = ordered[ordered.length - 1].elapsedSeconds as number;
  const elapsedSecondsFromElapsedField = Math.max(0, lastElapsed - firstElapsed);
  const elapsedSeconds = timestampElapsedSeconds ?? elapsedSecondsFromElapsedField;

  if (elapsedSeconds <= 0) {
    return { elapsedSeconds, movingSeconds: 0 };
  }

  const intervals = ordered
    .slice(1)
    .map((record, index) => (record.elapsedSeconds as number) - (ordered[index].elapsedSeconds as number))
    .filter(interval => interval > 0);

  const inferredIntervalSeconds = inferRecordingIntervalSeconds(ordered) ?? recordingIntervalSeconds;
  const longGapThresholdSeconds =
    inferredIntervalSeconds && inferredIntervalSeconds > 0
      ? inferredIntervalSeconds * LONG_GAP_MULTIPLIER
      : Number.POSITIVE_INFINITY;

  let movingSeconds = 0;
  for (let index = 0; index < ordered.length - 1; index += 1) {
    const current = ordered[index];
    const next = ordered[index + 1];
    const segmentSeconds = (next.elapsedSeconds as number) - (current.elapsedSeconds as number);

    if (segmentSeconds <= 0 || segmentSeconds > longGapThresholdSeconds) {
      continue;
    }

    const hasMovementSignal =
      (typeof current.speedMetersPerSecond === "number" && current.speedMetersPerSecond > 0) ||
      (typeof current.powerWatts === "number" && current.powerWatts > 0) ||
      (typeof current.cadenceRpm === "number" && current.cadenceRpm > 0) ||
      (typeof current.distanceMeters === "number" && typeof next.distanceMeters === "number" && next.distanceMeters > current.distanceMeters);

    if (hasMovementSignal) {
      movingSeconds += segmentSeconds;
    }
  }

  // If no clear movement signal exists, keep moving time aligned to elapsed span.
  if (movingSeconds <= 0 && intervals.length > 0) {
    movingSeconds = elapsedSeconds;
  }

  return {
    elapsedSeconds,
    movingSeconds: Math.min(movingSeconds, elapsedSeconds)
  };
}

function shouldReplaceSummaryDuration(summarySeconds: number | null, recordSeconds: number | null): boolean {
  if (recordSeconds === null || recordSeconds <= 0) {
    return false;
  }

  if (summarySeconds === null || summarySeconds <= 0) {
    return true;
  }

  const absoluteDelta = Math.abs(summarySeconds - recordSeconds);
  const mismatchThreshold = Math.max(DURATION_MISMATCH_SECONDS, recordSeconds * DURATION_MISMATCH_PERCENT);
  return absoluteDelta > mismatchThreshold;
}

function reconcileSummaryDurationsWithRecords(
  movingTimeSeconds: number | null,
  elapsedTimeSeconds: number | null,
  recordDurations: RecordDurationSummary
): { movingTimeSeconds: number | null; elapsedTimeSeconds: number | null; replaced: boolean } {
  const replaceElapsed = shouldReplaceSummaryDuration(elapsedTimeSeconds, recordDurations.elapsedSeconds);
  const replaceMoving = shouldReplaceSummaryDuration(movingTimeSeconds, recordDurations.movingSeconds);

  let nextElapsed = replaceElapsed ? recordDurations.elapsedSeconds : elapsedTimeSeconds;
  let nextMoving = replaceMoving ? recordDurations.movingSeconds : movingTimeSeconds;

  if (nextElapsed === null && recordDurations.elapsedSeconds !== null) {
    nextElapsed = recordDurations.elapsedSeconds;
  }

  if (nextMoving === null && recordDurations.movingSeconds !== null) {
    nextMoving = recordDurations.movingSeconds;
  }

  if (nextElapsed === null && nextMoving !== null) {
    nextElapsed = nextMoving;
  }

  if (nextMoving !== null && nextElapsed !== null && nextMoving > nextElapsed) {
    nextMoving = nextElapsed;
  }

  return {
    movingTimeSeconds: nextMoving,
    elapsedTimeSeconds: nextElapsed,
    replaced: replaceElapsed || replaceMoving
  };
}

function parseRecordMessages(data: unknown, profile?: RideParserPhaseProfile): RideRecord[] {
  const records: RideRecord[] = [];
  const seen = new Set<string>();
  const timestampFieldDiagnostics: Record<string, { count: number; types: Record<string, number> }> = {};
  const collectTimestampDiagnostics = import.meta.env.DEV;
  // Value is the `excluded` flag the candidate/node was processed with. A node processed while
  // not excluded can never yield anything new on a later visit, so it is never reprocessed.
  const processedCandidates = new WeakMap<object, boolean>();
  const descendedNodes = new WeakMap<object, boolean>();
  let traversedNodeCount = 0;
  let candidatesMapped = 0;
  let duplicatesSkipped = 0;
  let excludedByPath = 0;

  const shouldProcess = (registry: WeakMap<object, boolean>, node: object, excluded: boolean): boolean => {
    const previous = registry.get(node);
    if (previous !== undefined && (previous === false || previous === excluded)) {
      return false;
    }
    registry.set(node, excluded);
    return true;
  };

  const addRecordCandidate = (candidate: unknown, excluded: boolean) => {
    if (!isRecordLikeObject(candidate)) {
      return;
    }
    if (excluded) {
      excludedByPath += 1;
      return;
    }
    if (!shouldProcess(processedCandidates, candidate, excluded)) {
      return;
    }

    candidatesMapped += 1;
    const recordCandidate = candidate as Record<string, unknown>;
    if (collectTimestampDiagnostics) {
      for (const key of ["timestamp", "timeStamp", "time_stamp", "start_time", "startTime", "local_timestamp", "localTimestamp"]) {
        if (!(key in recordCandidate)) {
          continue;
        }

        const value = recordCandidate[key];
        const valueType = value instanceof Date ? "date" : Array.isArray(value) ? "array" : typeof value;
        if (!(key in timestampFieldDiagnostics)) {
          timestampFieldDiagnostics[key] = { count: 0, types: {} };
        }

        const detail = timestampFieldDiagnostics[key];
        detail.count += 1;
        detail.types[valueType] = (detail.types[valueType] ?? 0) + 1;
      }
    }

    const mappedRecord = mapToRideRecord(candidate);
    const signature = createRideRecordSignature(mappedRecord);

    if (!seen.has(signature)) {
      seen.add(signature);
      records.push(mappedRecord);
    } else {
      duplicatesSkipped += 1;
    }
  };

  const addCollectionCandidates = (collection: unknown, excluded: boolean) => {
    for (const item of normalizeRecordCollection(collection)) {
      addRecordCandidate(item, excluded);
    }
  };

  const root = data && typeof data === "object" && !Array.isArray(data) ? (data as Record<string, unknown>) : null;

  if (root) {
    const candidatePaths: string[][] = [
      ["records"],
      ["record"],
      ["activity", "records"],
      ["activity", "record"],
      ["messages", "records"],
      ["messages", "record"],
      ["data", "records"],
      ["data", "record"]
    ];

    for (const path of candidatePaths) {
      let current: unknown = root;
      let missingPath = false;

      for (const segment of path) {
        if (!current || typeof current !== "object" || Array.isArray(current)) {
          missingPath = true;
          break;
        }

        const objectNode = current as Record<string, unknown>;
        if (!(segment in objectNode)) {
          missingPath = true;
          break;
        }

        current = objectNode[segment];
      }

      if (!missingPath) {
        addCollectionCandidates(current, path.some(isExcludedMessagePathSegment));
      }
    }
  }

  const nodeStack: unknown[] = [data];
  const excludedStack: boolean[] = [false];

  while (nodeStack.length > 0) {
    const node = nodeStack.pop();
    const excluded = excludedStack.pop() === true;

    if (node === null || node === undefined || typeof node !== "object") {
      continue;
    }

    if (!shouldProcess(descendedNodes, node, excluded)) {
      continue;
    }

    traversedNodeCount += 1;

    if (Array.isArray(node)) {
      for (let index = 0; index < node.length; index += 1) {
        nodeStack.push(node[index]);
        excludedStack.push(excluded);
      }
      continue;
    }

    if (isRecordLikeObject(node)) {
      addRecordCandidate(node, excluded);
    }

    const entry = node as Record<string, unknown>;
    const keys = Object.keys(entry);
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      const value = entry[key];
      const nextExcluded = excluded || isExcludedMessagePathSegment(key);

      if (key === "record" || key === "records") {
        addCollectionCandidates(value, nextExcluded);
      }

      if (value === null || typeof value !== "object") {
        continue;
      }

      nodeStack.push(value);
      excludedStack.push(nextExcluded);
    }
  }

  // Signatures are unique by construction (see `seen`), so no post-sort de-duplication pass is required.
  const sortTimestampMs = new Map<RideRecord, number>();
  for (let index = 0; index < records.length; index += 1) {
    const record = records[index];
    sortTimestampMs.set(record, record.timestamp ? Date.parse(record.timestamp) : Number.NaN);
  }

  const normalizedRecords = records.sort((left, right) => {
    const leftTimestamp = sortTimestampMs.get(left) ?? Number.NaN;
    const rightTimestamp = sortTimestampMs.get(right) ?? Number.NaN;

    if (!Number.isNaN(leftTimestamp) && !Number.isNaN(rightTimestamp)) {
      return leftTimestamp - rightTimestamp;
    }

    if (left.elapsedSeconds !== undefined && left.elapsedSeconds !== null && right.elapsedSeconds !== undefined && right.elapsedSeconds !== null) {
      return (left.elapsedSeconds ?? 0) - (right.elapsedSeconds ?? 0);
    }

    return 0;
  });

  if (profile) {
    profile.counts.traversedNodeCount = traversedNodeCount;
    profile.counts.recordCandidatesMapped = candidatesMapped;
    profile.counts.recordCandidatesSkippedAsDuplicate = duplicatesSkipped;
    profile.counts.recordCandidatesExcludedByPath = excludedByPath;
    profile.counts.normalizedSamplesProduced = normalizedRecords.length;
  }

  if (import.meta.env.DEV) {
    console.info("[Ride Intelligence] Record timestamp field diagnostics", {
      extractedRecordCount: normalizedRecords.length,
      timestampFieldDiagnostics
    });
  }

  return normalizedRecords;
}

const SUMMARY_SIGNAL_KEYS = [
  "total_distance",
  "totalDistance",
  "total_timer_time",
  "totalTimerTime",
  "total_elapsed_time",
  "totalElapsedTime",
  "avg_speed",
  "avgSpeed",
  "average_speed",
  "averageSpeed",
  "enhanced_avg_speed",
  "enhancedAvgSpeed",
  "max_speed",
  "maxSpeed",
  "enhanced_max_speed",
  "enhancedMaxSpeed",
  "total_ascent",
  "totalAscent",
  "avg_heart_rate",
  "avgHeartRate",
  "max_heart_rate",
  "maxHeartRate",
  "avg_cadence",
  "avgCadence",
  "avg_power",
  "avgPower",
  "max_power",
  "maxPower",
  "total_calories",
  "totalCalories",
  "start_time",
  "startTime"
];

const TOTAL_TIMER_TIME_KEYS = ["total_timer_time", "totalTimerTime"];
const TOTAL_ELAPSED_TIME_KEYS = ["total_elapsed_time", "totalElapsedTime"];

function selectPrimarySummaryObject(data: unknown): unknown | null {
  const candidates: Array<{ node: unknown; totalTimerTime: number | null; totalElapsedTime: number | null }> = [];
  const stack: unknown[] = [data];

  while (stack.length > 0) {
    const current = stack.pop();
    if (current === null || current === undefined || typeof current !== "object") {
      continue;
    }

    if (Array.isArray(current)) {
      for (let index = 0; index < current.length; index += 1) {
        stack.push(current[index]);
      }
      continue;
    }

    const entry = current as Record<string, unknown>;
    let hasSummaryFields = false;
    for (let index = 0; index < SUMMARY_SIGNAL_KEYS.length; index += 1) {
      if (SUMMARY_SIGNAL_KEYS[index] in entry) {
        hasSummaryFields = true;
        break;
      }
    }

    if (hasSummaryFields) {
      candidates.push({
        node: current,
        totalTimerTime: toNumber(getValueFromObject(entry, TOTAL_TIMER_TIME_KEYS)),
        totalElapsedTime: toNumber(getValueFromObject(entry, TOTAL_ELAPSED_TIME_KEYS))
      });
    }

    const keys = Object.keys(entry);
    for (let index = 0; index < keys.length; index += 1) {
      const value = entry[keys[index]];
      if (value !== null && typeof value === "object") {
        stack.push(value);
      }
    }
  }

  if (candidates.length === 0) {
    return null;
  }

  if (candidates.length === 1) {
    return candidates[0].node;
  }

  const timedCandidates = candidates.filter(candidate => candidate.totalTimerTime !== null);
  if (timedCandidates.length > 0) {
    return timedCandidates.reduce((best, candidate) => {
      if (candidate.totalTimerTime === null) {
        return best;
      }
      return candidate.totalTimerTime > (best.totalTimerTime ?? -Infinity) ? candidate : best;
    }).node;
  }

  const elapsedCandidates = candidates.filter(candidate => candidate.totalElapsedTime !== null);
  if (elapsedCandidates.length > 0) {
    return elapsedCandidates.reduce((best, candidate) => {
      if (candidate.totalElapsedTime === null) {
        return best;
      }
      return candidate.totalElapsedTime > (best.totalElapsedTime ?? -Infinity) ? candidate : best;
    }).node;
  }

  return candidates[0].node;
}

export function applyRideAnalytics(ride: Ride, ftpInput: number | null | undefined = null): Ride {
  ride.rideAnalytics = computeRideAnalytics(ride.records, ftpInput, ride.movingTime, ride.recordingIntervalSeconds);
  ride.normalizedPower = ride.rideAnalytics.normalizedPower;
  ride.if = ride.rideAnalytics.intensityFactor;
  ride.vi = ride.rideAnalytics.variabilityIndex;
  ride.tss = ride.rideAnalytics.trainingStressScore;
  if (ride.rideAnalytics.averagePower !== null) ride.averagePower = ride.rideAnalytics.averagePower;
  ride.maximumPower = computeMaximumInstantaneousPower(ride.records);
  ride.powerDurationCurve = computePowerDurationCurve(ride.records);
  ride.analyticsValidationWarnings = collectAnalyticsValidationWarnings(ride);
  return ride;
}

export function extractRideSummary(data: unknown, ftpInput: number | null | undefined = null, includeAnalytics = true, profile?: RideParserPhaseProfile): Ride {
  const extractionStartedAt = performance.now();
  const summarySelectionStartedAt = performance.now();
  const selectedSummary = selectPrimarySummaryObject(data);
  const summaryData = selectedSummary ?? data;
  if (profile) {
    profile.summarySelectionMs = Math.max(0, performance.now() - summarySelectionStartedAt);
  }

  const ride = createEmptyRide();
  const summaryFieldStartedAt = performance.now();

  const summaryDistanceRaw = toNumber(findValueByKeys(summaryData, ["total_distance", "totalDistance"]));
  const summaryAverageSpeedRaw = toNumber(
    findValueByKeys(summaryData, ["enhanced_avg_speed", "enhancedAvgSpeed", "avg_speed", "average_speed", "avgSpeed", "averageSpeed"])
  );
  const summaryMaximumSpeedRaw = toNumber(
    findValueByKeys(summaryData, ["enhanced_max_speed", "enhancedMaxSpeed", "max_speed", "maxSpeed"])
  );

  ride.rideDate = findValueByKeys(summaryData, ["start_time", "startTime"]) as Ride["rideDate"];
  ride.movingTime = toNumber(findValueByKeys(summaryData, ["total_timer_time", "totalTimerTime"]));
  ride.elapsedTime = toNumber(findValueByKeys(summaryData, ["total_elapsed_time", "totalElapsedTime"]));
  ride.distance = summaryDistanceRaw;
  ride.averageSpeed = summaryAverageSpeedRaw;
  ride.maximumSpeed = summaryMaximumSpeedRaw;
  ride.elevationGain = toNumber(findValueByKeys(summaryData, ["total_ascent", "totalAscent"]));
  ride.averageHeartRate = toNumber(findValueByKeys(summaryData, ["avg_heart_rate", "avgHeartRate"]));
  ride.maxHeartRate = toNumber(findValueByKeys(summaryData, ["max_heart_rate", "maxHeartRate"]));
  ride.averageCadence = toNumber(findValueByKeys(summaryData, ["avg_cadence", "avgCadence"]));
  ride.averagePower = toNumber(findValueByKeys(summaryData, ["avg_power", "avgPower"]));
  ride.maximumPower = toNumber(findValueByKeys(summaryData, ["max_power", "maxPower"]));
  ride.calories = toNumber(findValueByKeys(summaryData, ["total_calories", "totalCalories"]));
  ride.vehicleCount = toNumber(findValueByKeys(summaryData, ["vehicle_count", "vehicleCount", "num_vehicles", "numVehicles"]));
  ride.heatStrainIndexValues = collectHeatStrainIndexValues(data);
  if (profile) {
    profile.summaryFieldExtractionMs = Math.max(0, performance.now() - summaryFieldStartedAt);
    const decodedCounts = summarizeDecodedFitMessageCounts(data);
    profile.counts = { ...profile.counts, ...decodedCounts };
  }

  if (import.meta.env.DEV) {
    const topLevelKeys = data && typeof data === "object" && !Array.isArray(data) ? Object.keys(data as Record<string, unknown>).slice(0, 50) : [];
    const collectionDiagnostics = collectCollectionDiagnostics(data);

    console.info("[Ride Intelligence] FIT parser shape", {
      topLevelKeys,
      collectionDiagnostics
    });
  }

  const recordExtractionStartedAt = performance.now();
  const extractedRecords = parseRecordMessages(data, profile);
  if (profile) {
    profile.recordExtractionMs = Math.max(0, performance.now() - recordExtractionStartedAt);
  }
  const normalizationStartedAt = performance.now();
  const timingNormalization = normalizeRecordTiming(extractedRecords, ride.elapsedTime, ride.movingTime);
  ride.records = timingNormalization.records;
  ride.recordingIntervalSeconds = timingNormalization.recordingIntervalSeconds;
  ride.timingFallbackUsed = timingNormalization.timingFallbackUsed;
  ride.timingNormalizationError = timingNormalization.timingNormalizationError;
  if (profile) {
    profile.recordNormalizationMs = Math.max(0, performance.now() - normalizationStartedAt);
  }
  const reconciliationStartedAt = performance.now();
  const recordDurations = deriveDurationsFromRecords(ride.records, ride.recordingIntervalSeconds);
  const reconciledDurations = reconcileSummaryDurationsWithRecords(ride.movingTime, ride.elapsedTime, recordDurations);
  ride.movingTime = reconciledDurations.movingTimeSeconds;
  ride.elapsedTime = reconciledDurations.elapsedTimeSeconds;
  const recordMetrics = deriveRecordMetrics(ride.records, ride.movingTime, ride.recordingIntervalSeconds);
  const reconciledMetrics = reconcileSummaryMetricsWithRecords(
    ride,
    summaryDistanceRaw,
    summaryAverageSpeedRaw,
    summaryMaximumSpeedRaw,
    recordMetrics
  );
  if (profile) {
    profile.summaryReconciliationMs = Math.max(0, performance.now() - reconciliationStartedAt);
  }

  if (import.meta.env.DEV && reconciledDurations.replaced) {
    console.info("[Ride Intelligence] Replaced inconsistent summary durations with record-derived durations", {
      summaryMovingTimeSeconds: toNumber(findValueByKeys(summaryData, ["total_timer_time", "totalTimerTime"])),
      summaryElapsedTimeSeconds: toNumber(findValueByKeys(summaryData, ["total_elapsed_time", "totalElapsedTime"])),
      recordElapsedSeconds: recordDurations.elapsedSeconds,
      recordMovingSeconds: recordDurations.movingSeconds,
      finalMovingTimeSeconds: ride.movingTime,
      finalElapsedTimeSeconds: ride.elapsedTime
    });
  }

  if (import.meta.env.DEV && (reconciledMetrics.replacedDistance || reconciledMetrics.replacedAverageSpeed || reconciledMetrics.replacedMaximumSpeed || reconciledMetrics.replacedHeartRate || reconciledMetrics.replacedCalories)) {
    console.info("[Ride Intelligence] Reconciled summary metrics with record stream", {
      summaryDistanceRaw,
      summaryAverageSpeedRaw,
      summaryMaximumSpeedRaw,
      recordDistanceMeters: recordMetrics.distanceMeters,
      recordAverageMovingSpeedMetersPerSecond: recordMetrics.averageMovingSpeedMetersPerSecond,
      recordMaximumSpeedMetersPerSecond: recordMetrics.maximumSpeedMetersPerSecond,
      recordAverageHeartRateBpm: recordMetrics.averageHeartRateBpm,
      recordMaximumHeartRateBpm: recordMetrics.maximumHeartRateBpm,
      recordEstimatedCaloriesKcal: recordMetrics.estimatedCaloriesKcal,
      usedGpsFallbackForDistance: recordMetrics.usedGpsFallbackForDistance,
      replacedDistance: reconciledMetrics.replacedDistance,
      replacedAverageSpeed: reconciledMetrics.replacedAverageSpeed,
      replacedMaximumSpeed: reconciledMetrics.replacedMaximumSpeed,
      replacedHeartRate: reconciledMetrics.replacedHeartRate,
      replacedCalories: reconciledMetrics.replacedCalories,
      finalDistanceMeters: ride.distance,
      finalAverageSpeedMetersPerSecond: ride.averageSpeed,
      finalMaximumSpeedMetersPerSecond: ride.maximumSpeed,
      finalAverageHeartRateBpm: ride.averageHeartRate,
      finalMaximumHeartRateBpm: ride.maxHeartRate,
      finalCaloriesKcal: ride.calories
    });
  }

  const analyticsStartedAt = performance.now();
  if (includeAnalytics) applyRideAnalytics(ride, ftpInput);
  if (profile) {
    profile.analyticsMs = includeAnalytics ? Math.max(0, performance.now() - analyticsStartedAt) : null;
  }
  const resultConstructionStartedAt = performance.now();
  ride.sourceFileName = null;
  ride.sourceFileSizeBytes = null;
  ride.rawData = data;

  const validationIssues = validateRideRecords(ride.records);
  const isDevelopmentEnvironment = import.meta.env.DEV;
  if (isDevelopmentEnvironment && ride.records.length > 0) {
    const powerCount = ride.records.filter(record => record.powerWatts !== null && record.powerWatts !== undefined).length;
    const heartRateCount = ride.records.filter(record => record.heartRateBpm !== null && record.heartRateBpm !== undefined).length;
    const gpsCount = ride.records.filter(record => record.latitudeDegrees !== null && record.longitudeDegrees !== null && record.latitudeDegrees !== undefined && record.longitudeDegrees !== undefined).length;
    const firstTimestamp = ride.records[0]?.timestamp ?? null;
    const lastTimestamp = ride.records[ride.records.length - 1]?.timestamp ?? null;

    console.info("[Ride Intelligence] Parsed ride records", {
      recordCount: ride.records.length,
      firstTimestamp,
      lastTimestamp,
      powerCount,
      heartRateCount,
      gpsCount,
      validationIssues
    });

    const powerDurationSummary = ride.powerDurationCurve?.filter(point => [300, 1200, 3600].includes(point.durationSeconds));
    if (powerDurationSummary && powerDurationSummary.length > 0) {
      console.info("[Ride Intelligence] Power Duration Summary", powerDurationSummary.map(point => ({
        durationMinutes: point.durationSeconds / 60,
        bestAveragePower: point.bestAveragePower
      })));
    }
  }

  if (profile) {
    profile.resultConstructionMs = Math.max(0, performance.now() - resultConstructionStartedAt);
    profile.totalMs = Math.max(0, performance.now() - extractionStartedAt);
  }

  return ride;
}
export async function parseRideBuffer(
  buffer: ArrayBuffer,
  fitParser: FitParser,
  ftpInput: number | null | undefined = null,
  includeAnalytics = true,
  signal?: AbortSignal,
  profile?: RideParserPhaseProfile
): Promise<Ride> {
  const binaryDecodeStartedAt = performance.now();
  const parsedData = await new Promise<unknown>((resolve, reject) => {
    let settled = false;
    const finishResolve = (data: unknown) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", onAbort);
      resolve(data);
    };
    const finishReject = (error: Error) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", onAbort);
      reject(error);
    };
    const onAbort = () => {
      finishReject(new Error("Ride parsing cancelled."));
    };

    if (signal?.aborted) {
      finishReject(new Error("Ride parsing cancelled."));
      return;
    }

    signal?.addEventListener("abort", onAbort, { once: true });

    try {
      fitParser.parse(buffer, (error: unknown, data: unknown) => {
        if (signal?.aborted) {
          finishReject(new Error("Ride parsing cancelled."));
          return;
        }
        if (error) {
          finishReject(error instanceof Error ? error : new Error("Unable to decode the selected FIT file."));
          return;
        }

        finishResolve(data);
      });
    } catch (error) {
      finishReject(error instanceof Error ? error : new Error("Unable to decode the selected FIT file."));
    }
  });

  if (profile) {
    profile.binaryDecodeMs = Math.max(0, performance.now() - binaryDecodeStartedAt);
  }

  return extractRideSummary(parsedData, ftpInput, includeAnalytics, profile);
}

export async function parseRideFile(file: File, fitParser: FitParser, ftpInput: number | null | undefined = null): Promise<Ride> {
  const ride = await parseRideBuffer(await file.arrayBuffer(), fitParser, ftpInput);
  ride.sourceFileName = file.name;
  ride.sourceFileSizeBytes = file.size;
  return ride;
}
